import React from 'react';
import PropTypes from 'prop-types';
import { Nav } from '@makeably/creativex-design-system';
import { arrayIf } from 'utilities/array';
import * as routes from 'utilities/routes';

export const abilitiesProps = PropTypes.shape({
  canViewChallenge: PropTypes.bool.isRequired,
  canViewProcessControl: PropTypes.bool.isRequired,
  canViewRepresentation: PropTypes.bool.isRequired,
  canViewTrialGuideline: PropTypes.bool.isRequired,
  canViewValidation: PropTypes.bool.isRequired,
  isFullInternal: PropTypes.bool.isRequired,
});

export const paramsProps = PropTypes.shape({
  id: PropTypes.string,
});

export const propTypes = {
  abilities: abilitiesProps.isRequired,
  companyName: PropTypes.string.isRequired,
  fullName: PropTypes.string.isRequired,
  homeUrl: PropTypes.string.isRequired,
  logoUrl: PropTypes.string,
  params: paramsProps.isRequired,
};

const defaultProps = {
  logoUrl: undefined,
};

function getAdmin() {
  return {
    icon: 'dashboard',
    label: 'To Admin',
    options: [
      {
        label: 'To Admin',
        urls: [
          routes.adminUsersPath(),
        ],
      },
    ],
  };
}

function getGeneral(abilities, ids) {
  const { isFullInternal } = abilities;
  const { id } = ids;

  return {
    icon: 'dashboard',
    label: 'General',
    options: [
      ...arrayIf(isFullInternal,
        {
          label: 'Act As Company Admin',
          urls: [
            routes.adminsInternalUsersPath(),
          ],
        },
        {
          label: 'Change Log',
          urls: [
            routes.internalVersionsPath(),
            routes.internalVersionPath(id),
          ],
        },
        {
          label: 'Data Explorer',
          urls: [
            routes.internalExplorerHomePath(),
            routes.internalAdPath(id),
            routes.internalExplorerAdPath(id),
            routes.internalExplorerAdAccountPath(id),
            routes.internalExplorerAdCampaignPath(id),
            routes.internalExplorerAdFormatPath(id),
            routes.internalExplorerAssetPath(id),
            routes.internalExplorerAuditPath(id),
            routes.internalExplorerAuditAssetPath(id),
            routes.internalExplorerAuditPostPath(id),
            routes.internalExplorerFacebookAdAccountPath(id),
            routes.internalExplorerFacebookAdCampaignPath(id),
            routes.internalExplorerFacebookAdCreativePath(id),
            routes.internalExplorerFacebookAdGroupPath(id),
            routes.internalExplorerGuidelinePath(id),
            routes.internalExplorerGuidelineCheckPath(id),
            routes.internalExplorerGuidelineDetailPath(id),
            routes.internalExplorerMarketPath(id),
            routes.internalExplorerCompanyPath(id),
            routes.internalExplorerOrganizationBrandPath(id),
            routes.internalExplorerPublisherPath(id),
            routes.internalExplorerUserPath(id),
            routes.internalFacebookAdCreativePath(id),
            routes.internalFacebookAdGroupPath(id),
            routes.rolesInternalUserPath(id),
          ],
        }),
    ],
  };
}

function getConnections(abilities, ids) {
  const { isFullInternal } = abilities;
  const { id } = ids;

  return {
    icon: 'dashboard',
    label: 'Connections',
    options: [
      ...arrayIf(isFullInternal,
        {
          label: 'Coverage Dashboard',
          urls: [
            routes.internalAdAccountMetricsPath(),
          ],
        },
        {
          label: 'DV360',
          urls: [
            routes.internalDV360AdAccountsPath(),
            routes.internalDV360AdAccountPath(id),
            routes.newInternalDV360AdAccountPath(),
            routes.newInternalDV360CampaignPath(),
          ],
        },
        {
          label: 'Google Ads',
          urls: [
            routes.internalGoogleAdsAccountsPath(),
            routes.newInternalGoogleAdsAccountPath(),
            routes.internalGoogleAdsIntegrationsPath(),
          ],
        }),
    ],
  };
}

function getReview(abilities, ids) {
  const { isFullInternal, canViewProcessControl } = abilities;
  const { id, reviewGuideId } = ids;

  return {
    icon: 'dashboard',
    label: 'Review',
    options: [
      ...arrayIf(canViewProcessControl,
        {
          label: 'Accuracy Review Queue',
          urls: [
            routes.internalReviewProcessControlChecksPath(),
            routes.editInternalReviewProcessControlCheckPath(id),
          ],
        }),
      ...arrayIf(isFullInternal,
        {
          label: 'Challenge Queue',
          urls: [
            routes.internalReviewChallengesPath(),
            routes.editInternalReviewChallengePath(id),
            routes.safeZoneInternalReviewChallengePath(id),
          ],
        },
        {
          label: 'Review Guides',
          urls: [
            routes.internalReviewGuidesPath(),
            routes.editInternalReviewGuidePath(id),
            routes.internalReviewGuideSubscriptionsPath(reviewGuideId),
            routes.newInternalReviewGuidePath(),
            routes.newInternalReviewGuideSubscriptionPath(reviewGuideId),
          ],
        }),
    ],
  };
}

function getCompanies(abilities, ids) {
  const { isFullInternal } = abilities;
  const {
    guidelineId,
    id,
    scoreId,
  } = ids;

  return {
    icon: 'dashboard',
    label: 'Companies',
    options: [
      ...arrayIf(isFullInternal,
        {
          label: 'Guidelines',
          urls: [
            routes.internalGuidelinesPath(),
            routes.internalGuidelinePath(id),
            routes.internalGuidelineSubscriptionsPath(guidelineId),
            routes.editInternalGuidelinePath(id),
            routes.newInternalGuidelineSubscriptionPath(guidelineId),
            routes.newInternalGuidelinePath(),
          ],
        },
        {
          label: 'Scores',
          urls: [
            routes.internalScoresPath(),
            routes.internalScorePath(id),
            routes.internalScoreSubscriptionsPath(scoreId),
            routes.newInternalScoreSubscriptionPath(scoreId),
          ],
        },
        {
          label: 'User Bulk Edit',
          urls: [
            routes.bulkEditInternalUsersPath(),
          ],
        },
        {
          label: 'Users',
          urls: [
            routes.adminUsersPath(),
          ],
        }),
    ],
  };
}

function getEngineering(abilities, ids) {
  const { isFullInternal } = abilities;
  const { id } = ids;

  return {
    icon: 'dashboard',
    label: 'Engineering',
    options: [
      ...arrayIf(isFullInternal,
        {
          label: 'Code Usage',
          urls: [
            routes.coverbandWebPath(),
          ],
          useNewTab: true,
        },
        {
          label: 'Error Queries',
          urls: [
            routes.internalErrorQueriesPath(),
            routes.internalErrorQueryPath(id),
          ],
        },
        {
          label: 'Pipeline Trackers',
          urls: [
            routes.internalPipelineTrackersPath(),
            routes.trackerAdsInternalPipelineTrackersPath(),
          ],
        },
        {
          label: 'Sidekiq',
          urls: [
            routes.sidekiqWebPath(),
          ],
          useNewTab: true,
        }),
    ],
  };
}

export function getMenu(abilities, params) {
  const ids = {
    guidelineId: params.guidelineId ?? 0,
    id: params.id ?? '0',
    reviewGuideId: params.reviewGuideId ?? 0,
    scoreId: params.scoreId ?? 0,
  };

  const all = [
    getAdmin(),
    getGeneral(abilities, ids),
    getConnections(abilities, ids),
    getReview(abilities, ids),
    getCompanies(abilities, ids),
    getEngineering(abilities, ids),
  ];

  return all.filter((section) => section !== null);
}

function getUserOptions() {
  return [
    {
      label: 'Sign Out',
      url: routes.logoutPath(),
    },
  ];
}

function InternalNavigation({
  abilities,
  companyName,
  fullName,
  homeUrl,
  logoUrl,
  params,
}) {
  const menu = getMenu(abilities, params);
  const user = {
    companyName,
    fullName,
    logoUrl,
  };
  const userOptions = getUserOptions();

  return (
    <Nav
      color="white"
      currentParams={window.location.search}
      currentUrl={window.location.pathname}
      homeUrl={homeUrl}
      menu={menu}
      user={user}
      userOptions={userOptions}
    />
  );
}

InternalNavigation.propTypes = propTypes;
InternalNavigation.defaultProps = defaultProps;

export default InternalNavigation;
